/**
 * Copyright(c) 2024 Magrabi
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information of Magrabi ("Confidential
 * Information"). You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the contract agreement you entered into with Magrabi.
 *
 * @author Indrajit
 */

import { cookies as NextCookies } from "../apiUtils/cookieBaseUtils";
import { cookieValueKeyAliases } from "../apiUtils/constants";
import { objectCompression, objectDecompress } from "../apiUtils/compressionUtils";
import { cookies as ClientCookies } from "@/utils/client-cookie.utils";
import { getCookieClient } from "./cookieClientUtils";

let cookies = NextCookies;

if (typeof document !== "undefined") {
  cookies = ClientCookies as any;
}

const COOKIE_METADATA_KEY = "_md";

const getMetaDataKey = (key: string) => cookieValueKeyAliases[key];

const decompressCookieMetadata = (compressedMetadata: string) => {
  let metadata: {
    [key: string]: string;
  } = {};

  try {
    if (compressedMetadata) {
      metadata = objectDecompress(compressedMetadata);
    }
  } catch (error) {
    console.error("cookie err", error);
  }

  return metadata;
};

const prepareDataToSetCookie = (compressedMetadata: string) => (key: string, value: string) =>
  objectCompression({
    ...decompressCookieMetadata(compressedMetadata),
    [key]: value,
  });

const prepareDataToRemoveCookie = (compressedMetadata: string) => (key: string) => {
  const metadata = decompressCookieMetadata(compressedMetadata);
  if (metadata[key]) {
    delete metadata[key];
  }
  return objectCompression(metadata);
};

const readFromCookieMetadata = (compressedMetadata: string) => (key: string) => {
  return decompressCookieMetadata(compressedMetadata)[key];
};

export function getFromCookieMetadata(key: string): string {
  const metadataKey = getMetaDataKey(key);
  console.log("getFromCookieMetadata key", key, metadataKey);
  if (metadataKey) {
    const currentMetadata = cookies().get(COOKIE_METADATA_KEY)?.value as string;

    const res = readFromCookieMetadata(currentMetadata)(metadataKey);
    console.log("getFromCookieMetadata metadataKey res", currentMetadata, res);

    return res;
  }

  console.log("getFromCookieMetadata res", cookies().get(key)?.value as string);
  return cookies().get(key)?.value as string;
}

export function setInCookieMetadata(key: string, value: string) {
  const metadataKey = getMetaDataKey(key);
  if (metadataKey) {
    const currentMetadata = cookies().get(COOKIE_METADATA_KEY)?.value as string;
    const compressedPayload = prepareDataToSetCookie(currentMetadata)(getMetaDataKey(key), value);
    cookies().set(COOKIE_METADATA_KEY, compressedPayload);
  } else {
    cookies().set(key, value);
  }
}

export function removeFromCookie(key: string) {
  const metadataKey = getMetaDataKey(key);
  if (metadataKey) {
    const currentMetadata = cookies().get(COOKIE_METADATA_KEY)?.value as string;
    const compressedPayload = prepareDataToRemoveCookie(currentMetadata)(metadataKey);
    cookies().set(COOKIE_METADATA_KEY, compressedPayload);
  } else {
    cookies().delete(key);
  }
}

/**
 * Client-side function to get value from cookie metadata - Main Function
 * Equivalent to server-side getFromCookieMetadata
 * @param key - The key to retrieve (e.g., "c_sa")
 * @returns Value from metadata or direct cookie
 */
export function getFromCookieMetadataClient(key: string): string | null {
  try {
    if (typeof document === "undefined") {
      return null;
    }

    if (!key || typeof key !== "string") {
      return null;
    }

    const metadataKey = getMetaDataKey(key);

    if (metadataKey) {
      const currentMetadata = getCookieClient(COOKIE_METADATA_KEY);
      if (!currentMetadata) return null;

      const value = readFromCookieMetadata(currentMetadata)(metadataKey);
      return value || null;
    } else {
      const value = getCookieClient(key);
      return value;
    }
  } catch (error) {
    return null;
  }
}
